/**
 * CourseCompletionTab
 *
 * Tab 4 of the course editor: per-lesson completion criteria + certificate
 * config.
 *
 * Criteria model: each lesson can be marked Required or Optional. When
 * required, type-specific defaults apply — Videos require ≥95% watched,
 * Quizzes require a passing score. Admins can override either value or
 * skip the lesson entirely.
 *
 * Storage: criteria persist on the lesson itself (`Lesson.isRequired` /
 * `minWatchPercent`, and `quizSettings.preventProgression` for "must pass").
 * Auto-saves on every change via a read-modify-write update of the lesson.
 *
 * Certificate: surfaces the tenant's active certificate template. Per-
 * course certificate selection requires a backend field — flagged in
 * the UI.
 */

import { useEffect, useState } from 'react';
import { Course, LessonType } from '../../apis/api-client';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  TextField,
  Typography,
} from '../ui';
import { lessonTypeMeta } from './contentTypeMeta';
import { useLessonManagement } from '../../hooks/useLessonManagement';
import { useModuleManagement } from '../../hooks/useModuleManagement';
import { CertificateTemplateEditor } from '../settings';
import { getIssuesCertificate } from '../../utils/coursePrefs';
import { courseToSettings } from '../../utils/courseSettings';
import { coursesClient } from '../../apis/client-instances';

export interface CourseCompletionTabProps {
  course: Course | null;
  /** Parent re-fetches the course so the loaded record stays fresh after
   *  toggling certificate issuance (keeps sibling-tab saves from clobbering
   *  it via courseToSettings). */
  onCourseChanged?: () => void;
}

// Effective completion criteria for a lesson, read off its server fields
// with type-specific defaults backfilled for display when a value is unset.
interface LessonCriteria {
  required: boolean;
  /** Only meaningful for Video lessons. Percentage 0-100. */
  minWatchPercent: number;
  /** Only meaningful for Quiz lessons — maps to quizSettings.preventProgression. */
  requirePass: boolean;
}

function criteriaForLesson(lesson: any): LessonCriteria {
  return {
    required: lesson.isRequired ?? true,
    minWatchPercent: lesson.minWatchPercent ?? 95,
    requirePass: lesson.quizSettings?.preventProgression ?? true,
  };
}

// ── Single-lesson criteria row ──────────────────────────────────────────
function LessonCriteriaRow({
  lesson,
  primaryTitle,
  secondaryTitle,
  showDivider,
  onUpdate,
}: {
  lesson: any;
  /** Title rendered as the row's primary line. Defaults to the lesson
   *  title but the parent overrides this with the module name when the
   *  module has exactly one lesson — that text usually carries the
   *  descriptive context. */
  primaryTitle?: string;
  /** Optional small caption under the title — used to surface the
   *  lesson title when the parent has hoisted the module name into
   *  the primary slot and the two genuinely differ. */
  secondaryTitle?: string;
  showDivider?: boolean;
  /** Persist a rule change on the lesson (read-modify-write via the hook). */
  onUpdate: (patch: { isRequired?: boolean; minWatchPercent?: number; quizSettings?: any }) => void;
}) {
  const meta = lessonTypeMeta(lesson.lessonType);
  const criteria = criteriaForLesson(lesson);

  const setRequired = (required: boolean) => onUpdate({ isRequired: required });
  const setMinWatchPercent = (minWatchPercent: number) => onUpdate({ minWatchPercent });
  const setRequirePass = (requirePass: boolean) =>
    onUpdate({ quizSettings: { ...lesson.quizSettings, preventProgression: requirePass } });

  const isVideo = lesson.lessonType === LessonType.Video;
  const isQuiz = lesson.lessonType === LessonType.Quiz;
  const title = primaryTitle || lesson.title || 'Untitled lesson';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 0.5,
        px: 1,
        opacity: criteria.required ? 1 : 0.6,
        transition: 'opacity 150ms ease',
        borderBottom: showDivider ? '1px solid' : 'none',
        borderBottomColor: 'divider',
        '&:hover': { bgcolor: '#f0f9ff' },
      }}
    >
      {/* Required checkbox */}
      <Checkbox
        checked={criteria.required}
        onChange={(e) => setRequired(e.target.checked)}
        size="small"
        sx={{ p: 0.5 }}
        inputProps={{ 'aria-label': 'Required for completion' }}
      />

      {/* Type icon */}
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: 0.75,
          bgcolor: `${meta.color}15`,
          color: meta.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          '& svg': { fontSize: 16 },
        }}
      >
        {meta.icon}
      </Box>

      {/* Title (with optional secondary line). The "Optional" hint
          replaces the secondary line when the lesson isn't required —
          they carry the same role: clarify why the row reads dimmer. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {!criteria.required ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Optional — not required for course completion
          </Typography>
        ) : secondaryTitle ? (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.2 }}>
            {secondaryTitle}
          </Typography>
        ) : null}
      </Box>

      {/* Type-specific completion control — only shown when required.
          Compact single-line "Watch ≥ 95 %" wrapping the number input
          in fixed text on both sides so the column stays narrow even
          when the title runs long. */}
      {criteria.required && isVideo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Watch ≥
          </Typography>
          <TextField
            size="small"
            type="number"
            value={criteria.minWatchPercent}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 95;
              setMinWatchPercent(clamped);
            }}
            inputProps={{
              min: 0,
              max: 100,
              step: 5,
              style: { width: 38, textAlign: 'right', padding: '4px 6px' },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            %
          </Typography>
        </Box>
      )}
      {criteria.required && isQuiz && (
        <FormControlLabel
          control={
            <Checkbox
              checked={criteria.requirePass}
              onChange={(e) => setRequirePass(e.target.checked)}
              size="small"
              sx={{ p: 0.5 }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Must pass
            </Typography>
          }
          sx={{ mr: 0, flexShrink: 0 }}
        />
      )}
      {criteria.required && !isVideo && !isQuiz && (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          View the lesson
        </Typography>
      )}
    </Box>
  );
}

export default function CourseCompletionTab({ course, onCourseChanged }: CourseCompletionTabProps) {
  const courseId = course?.id ?? 0;
  const lessonManagement = useLessonManagement(courseId);
  const moduleManagement = useModuleManagement(courseId);

  // Initial fetch when the course id becomes available.
  useEffect(() => {
    if (!courseId) return;
    lessonManagement.fetchLessons();
    moduleManagement.fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Per-course toggle for "issue a certificate at completion." Defaults
  // off so a fresh course doesn't accidentally award an un-styled
  // certificate; admins opt in and the editor only renders once they do.
  // Persisted on the course settings blob.
  const [issuesCertificate, setIssuesCertificateState] = useState(false);
  useEffect(() => {
    setIssuesCertificateState(getIssuesCertificate(course));
  }, [course]);
  const handleIssuesCertificateToggle = async (next: boolean) => {
    if (!course?.id) return;
    setIssuesCertificateState(next); // optimistic
    try {
      await coursesClient.updateCourse(course.id, {
        ...(course as any),
        settings: { ...courseToSettings(course), issuesCertificate: next },
      });
      onCourseChanged?.();
    } catch {
      setIssuesCertificateState(!next); // revert on failure
    }
  };

  if (!course) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Save the course settings first to configure completion behavior.
        </Typography>
      </Box>
    );
  }

  // Group lessons by module for display, matching the curriculum builder's
  // grouping so admins see the same structure they're used to.
  const lessonsByModule = new Map<number | null, any[]>();
  for (const l of lessonManagement.lessons) {
    const key = l.moduleId ?? null;
    if (!lessonsByModule.has(key)) lessonsByModule.set(key, []);
    lessonsByModule.get(key)!.push(l);
  }
  const orphanLessons = lessonsByModule.get(null) ?? [];

  // Count required vs total so the header shows "5 of 7 required".
  // Reads straight off the lesson list, which the hook keeps fresh.
  let requiredCount = 0;
  for (const l of lessonManagement.lessons) {
    if (l.isRequired ?? true) requiredCount++;
  }
  const totalLessons = lessonManagement.lessons.length;

  // Restore every lesson to its type defaults: required, videos at 95%
  // watched, quizzes requiring a pass. Each is a read-modify-write update.
  const handleResetDefaults = () => {
    for (const l of lessonManagement.lessons) {
      const patch: { isRequired: boolean; minWatchPercent?: number; quizSettings?: any } = {
        isRequired: true,
      };
      if (l.lessonType === LessonType.Video) patch.minWatchPercent = 95;
      if (l.lessonType === LessonType.Quiz) {
        patch.quizSettings = { ...l.quizSettings, preventProgression: true };
      }
      lessonManagement.updateLessonRules(l.id, patch);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Per-lesson completion criteria ───────────────────────────── */}
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Completion criteria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalLessons === 0
                ? 'Add lessons in the Curriculum tab to set completion rules.'
                : `${requiredCount} of ${totalLessons} ${totalLessons === 1 ? 'lesson' : 'lessons'} required for course completion.`}
            </Typography>
          </Box>
          {totalLessons > 0 && (
            <Button size="small" variant="text" onClick={handleResetDefaults}>
              Reset to defaults
            </Button>
          )}
        </Box>

        {totalLessons > 0 && (
          // Single flush list with hair dividers between rows. Each
          // module collapses to a one-row representation when it
          // contains a single lesson — the module name takes the
          // primary slot since it usually carries the descriptive
          // text and the lesson title is just a short index ("Tip 1").
          // Multi-lesson modules render an eyebrow section header
          // above their rows, but no bordered card wrapper. End
          // result: roughly half the vertical space the bordered-
          // per-module layout took, with the same legibility.
          <Box
            sx={{
              mt: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {(() => {
              // Build a flat ordered list of render items so we can
              // make divider decisions ("is this the last row?") in
              // one pass without nested awareness across modules.
              type Item =
                | { kind: 'header'; key: string; label: string; muted?: boolean }
                | { kind: 'row'; key: string; lesson: any; primary?: string; secondary?: string };

              const items: Item[] = [];
              for (const mod of moduleManagement.modules) {
                const moduleLessons = lessonsByModule.get(mod.id) ?? [];
                if (moduleLessons.length === 0) continue;
                if (moduleLessons.length === 1) {
                  // Collapse: module name → primary, lesson title →
                  // secondary only when the two genuinely differ
                  // (case-insensitive trimmed compare). When they're
                  // basically the same string, drop the secondary line
                  // entirely so the row stays one-line-tall.
                  const lesson = moduleLessons[0];
                  const modTitle = (mod.title || '').trim();
                  const lessonTitle = (lesson.title || '').trim();
                  const sameish =
                    !modTitle ||
                    !lessonTitle ||
                    modTitle.toLowerCase() === lessonTitle.toLowerCase() ||
                    modTitle.toLowerCase().includes(lessonTitle.toLowerCase()) ||
                    lessonTitle.toLowerCase().includes(modTitle.toLowerCase());
                  items.push({
                    kind: 'row',
                    key: `lesson-${lesson.id}`,
                    lesson,
                    primary: modTitle || lessonTitle || 'Untitled lesson',
                    secondary: sameish ? undefined : lessonTitle,
                  });
                } else {
                  items.push({
                    kind: 'header',
                    key: `mod-${mod.id}`,
                    label: mod.title || 'Untitled module',
                  });
                  for (const lesson of moduleLessons) {
                    items.push({ kind: 'row', key: `lesson-${lesson.id}`, lesson });
                  }
                }
              }
              if (orphanLessons.length > 0) {
                items.push({
                  kind: 'header',
                  key: 'orphan-header',
                  label: 'Lessons without a module',
                  muted: true,
                });
                for (const lesson of orphanLessons) {
                  items.push({ kind: 'row', key: `lesson-${lesson.id}`, lesson });
                }
              }

              return items.map((item, idx) => {
                const next = items[idx + 1];
                // Hide row divider when the next item is a section
                // header (the header's top divider handles the gap).
                const showDivider = !!next && next.kind === 'row';
                if (item.kind === 'header') {
                  return (
                    <Box
                      key={item.key}
                      sx={{
                        px: 1.5,
                        pt: idx === 0 ? 1 : 1.5,
                        pb: 0.5,
                        // Hair top-divider on non-first sections so
                        // they read as a fresh group without needing
                        // a grey band.
                        borderTop: idx === 0 ? 'none' : '1px solid',
                        borderTopColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          color: item.muted ? 'text.disabled' : 'text.secondary',
                          lineHeight: 1.2,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <LessonCriteriaRow
                    key={item.key}
                    lesson={item.lesson}
                    primaryTitle={item.primary}
                    secondaryTitle={item.secondary}
                    showDivider={showDivider}
                    onUpdate={(patch) => lessonManagement.updateLessonRules(item.lesson.id, patch)}
                  />
                );
              });
            })()}
          </Box>
        )}
      </Card>

      {/* ── Certificate ──────────────────────────────────────────────── */}
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          Certificate of completion
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Learners who complete the course receive a certificate they can
          download or share.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={issuesCertificate}
              onChange={(e) => handleIssuesCertificateToggle(e.target.checked)}
              color="primary"
            />
          }
          label="Issue a certificate when this course is completed"
        />

        {issuesCertificate && (
          <Box sx={{ mt: 2 }}>
            <CertificateTemplateEditor course={course} />
          </Box>
        )}
      </Card>
    </Box>
  );
}
